import {
  TooltipForChild,
  XForm as RawXForm,
  springConfigs,
  useEsc,
  useReveal,
  cx,
  KeyPressContextProvider,
  useGlobalKeyPress,
} from "@cdx/common";
import {
  Box,
  Col,
  DSButton,
  DSIconButton,
  DSIconChevronLeft,
  DSIconFullscreen,
  Row,
  css,
  DSSpinner,
  SimplePortal,
} from "@cdx/ds";
import type {Location, History} from "history";
import {Dispatch, ReactNode, Suspense, forwardRef, useRef, useState} from "react";
import {animated} from "react-spring";
import {api} from "../../lib/api";
import {cardEditPersistStore} from "./OpenCard";
import {moveCardsToDeck} from "../../lib/cross-project-cards-or-decks";
import Markdown from "../../components/Markdown";
import {isSelectionActive} from "../../lib/utils";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import Uploader, {transformUploadForTextEditor} from "../../components/uploader";
import {cardContentEditFormRules} from "./CardContentForm";
import {UnsavedChanges} from "./card-container-utils";
import {focusModeStyles} from "./unified-card-container.css";
import dsStyles from "@cdx/ds/css/index.css";
import {useNeoBag} from "@cdx/common/forms/neo-form";
import FocusLock from "react-focus-lock";
import {focusLockKeyGroupContext} from "../../components/Modals";
import {
  CdxLexicalEditor,
  CdxLexicalState,
  SelectContent,
  lexicalSerializer,
  useCdxEditorState,
} from "../../components/RichTextarea/Lexical/LexicalRichTextProvider";
import {Root} from "../../cdx-models/Root";
import {Card, CardId} from "../../cdx-models/Card";
import {FullScreenNodeContext, useIsFullScreen} from "../../lib/hooks/useFullscreen";

type UploadInfo = {
  ongoingUploads: any;
  dropAreaProps: any;
};

const ShowUploads = Uploader.Uploads as any;

const XForm = RawXForm as any;

type LayoutProps = {
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  closeTooltip: string;
  onClick?: (e: any) => void;
  uploadArea?: ReactNode;
};
const Layout = ({onClose, children, footer, closeTooltip, onClick, uploadArea}: LayoutProps) => {
  const [isFullScreen, toggleFullscreen] = useIsFullScreen();

  return (
    <Col flex="auto" minHeight="0">
      <Row className={focusModeStyles.header}>
        <TooltipForChild tooltip={closeTooltip} placement="right">
          <DSIconButton
            icon={<DSIconChevronLeft />}
            variant="secondary"
            onClick={onClose}
            size="sm"
            className={dsStyles.pointerEvents.auto}
          />
        </TooltipForChild>
        <Box ml="auto" pointerEvents="auto">
          <TooltipForChild tooltip="Full Screen" placement="left">
            <DSIconButton
              icon={<DSIconFullscreen />}
              variant="tertiary"
              onClick={toggleFullscreen}
              size="sm"
              active={isFullScreen}
              negatePadding
            />
          </TooltipForChild>
        </Box>
      </Row>
      {/* This complex scroll setup might not be needed in case we don't want to have a bar on top */}
      <Box flex="auto" relative>
        <Col
          align="center"
          absolute
          inset="0"
          className={makeScrollable({mode: "wide"})}
          {...({onClick} as any)}
        >
          {uploadArea}
          <Col maxWidth="40rem" width="100%" flex="auto" relative>
            {children}
          </Col>
          {footer && (
            // this sticky/absolute combo allows to respect the scrollbar being present
            <div
              onClick={(e) => e.stopPropagation()}
              className={css({
                position: "sticky",
                bottom: "0",
                left: "0",
                width: "100%",
                pointerEvents: "none",
              })}
            >
              <Row absolute left="0" right="0" bottom="0" py="12px" px="16px" sp="8px">
                {footer}
              </Row>
            </div>
          )}
        </Col>
      </Box>
    </Col>
  );
};

const CustomTextarea = forwardRef<any, any>(
  ({hasPendingValidation, showErrors, Editor, ...props}, ref) => (
    <>
      <Editor {...props} ref={ref} />
      {showErrors.length > 0 && (
        <Box colorTheme="alert25" bg="foreground" color="primary" bold pa="8px" rounded={4}>
          {showErrors[0]}
        </Box>
      )}
    </>
  )
);

const SubmitButton = ({children}: {children: ReactNode}) => {
  const bag = useNeoBag();

  return (
    <DSButton type="submit" onClick={() => bag.handleSubmit()}>
      {children}
    </DSButton>
  );
};

type FocusEditorProps = {
  card: Card;
  contentField: CdxLexicalState;
  onReceiveFile: any;
  togglePreview: any;
  isEmpty: any;
  onSubmit: any;
  onCardChange: any;
  onClose: () => void;
  buttonLabel: string;
};

const FocusEditor = (props: FocusEditorProps) => {
  const {
    card,
    contentField,
    onReceiveFile,
    togglePreview,
    isEmpty,
    onSubmit,
    onCardChange,
    onClose,
    buttonLabel,
  } = props;

  const footer = (
    <Row ml="auto" pointerEvents="auto" sp="8px">
      <DSButton onClick={togglePreview} variant="secondary">
        Preview
      </DSButton>
      <SubmitButton>{buttonLabel}</SubmitButton>
    </Row>
  );

  return (
    <XForm
      onSubmit={onSubmit}
      values={{content: {...contentField.state, isEmpty}}}
      rules={cardContentEditFormRules}
      className={css({display: "flex", flexDir: "column", flex: "auto"})}
    >
      <Layout onClose={onClose} footer={footer} closeTooltip="Close">
        <XForm.Field
          as={CustomTextarea}
          Editor={CdxLexicalEditor}
          maybeProject={card.deck && card.deck.project}
          name="content"
          placeholder="Card content"
          onPasteFile={transformUploadForTextEditor(onReceiveFile)}
          submitOnCmdEnter
          className={css(
            {px: "12px", display: "flex", flexDir: "column", flex: "auto", textType: "prose14"},
            focusModeStyles.topContent,
            makeScrollable()
          )}
          card={card}
          autoFocus
          onCardChange={onCardChange}
        />
      </Layout>
    </XForm>
  );
};

type PreviewProps = {
  contentAsText: any;
  onSubmit: () => unknown;
  project: any;
  onClose: () => void;
  buttonLabel: string;
};

const Preview = (props: PreviewProps) => {
  const {contentAsText, onSubmit, project, onClose, buttonLabel} = props;
  useEsc(onClose);
  const footer = (
    <>
      <Row ml="auto" pointerEvents="auto" sp="8px">
        <DSButton onClick={onClose} variant="secondary" active>
          Preview
        </DSButton>
        <DSButton onClick={onSubmit}>{buttonLabel}</DSButton>
      </Row>
    </>
  );
  return (
    <Layout onClose={onClose} closeTooltip="Close Preview" footer={footer}>
      <Markdown
        projectId={project && project.id}
        size="lg"
        className={cx(focusModeStyles.topContent, dsStyles.px["16px"])}
        parseCheckboxes
        autoTitle
      >
        {contentAsText}
      </Markdown>
    </Layout>
  );
};

type EditorViewProps = {
  onReceiveFile: unknown;
  card: Card;
  onClose: (opts?: {text: string}) => void;
  initialEditorState: any;
  selectContent: SelectContent | null;
  editOnly?: boolean;
  saveButtonLabel?: string;
  onSubmit: (content: string) => Promise<unknown>;
  getInitialContent: () => any;
  contentKey: string | false | null;
};
const EditorView = (props: EditorViewProps) => {
  const {
    card,
    onReceiveFile,
    onClose,
    initialEditorState,
    selectContent,
    saveButtonLabel = "Save",
    onSubmit,
    contentKey,
    getInitialContent,
  } = props;
  const [showPreview, setShowPreview] = useState(false);
  const togglePreview = () => setShowPreview((v) => !v);

  const [isEmpty, setIsEmpty] = useState(false);
  const contentField = useCdxEditorState({
    initialContent: () => {
      if (initialEditorState) {
        return {type: "state", value: initialEditorState};
      } else {
        const content = getInitialContent();
        if (typeof content === "string") {
          return {type: "text", value: content, selectContent};
        } else {
          return content;
        }
      }
    },
    contentKey,
    onChangeListeners: {
      isEmpty: setIsEmpty,
    },
  });
  cardEditPersistStore.usePersistValues(card, () => {
    const text = !isEmpty && (contentField.getText() || "").trim();
    if (text && text !== card.content.trim()) {
      return lexicalSerializer.toJSON(contentField);
    }
  });

  const handleClose = () => onClose({text: contentField.getText()});
  useEsc(handleClose);

  const handleSubmit = () =>
    onSubmit(contentField.getText()).then(() => {
      cardEditPersistStore.clearStoredValue(card);
      onClose();
    });

  const handleCardChange = (data: any) => {
    const {assignee, deckId, ...rest} = data;
    if (deckId) {
      return moveCardsToDeck([card.cardId as CardId], deckId);
    } else {
      if (assignee !== undefined) rest.assigneeId = assignee;
      return api.mutate.cards.update({id: card.cardId, ...rest});
    }
  };

  return showPreview ? (
    <Preview
      contentAsText={contentField.getText()}
      onSubmit={handleSubmit}
      onClose={togglePreview}
      project={card.deck && card.deck.project}
      buttonLabel={saveButtonLabel}
    />
  ) : (
    <FocusEditor
      card={card}
      contentField={contentField}
      onReceiveFile={onReceiveFile}
      togglePreview={togglePreview}
      isEmpty={isEmpty}
      onSubmit={handleSubmit}
      onCardChange={handleCardChange}
      onClose={handleClose}
      buttonLabel={saveButtonLabel}
    />
  );
};

type CardViewProps = {
  card: Card;
  onEdit: (pos?: any) => void;
  canModify: boolean | null;
  onClose: () => void;
  uploadInfo?: UploadInfo;
  root: Root;
};

const CardView = ({card, onEdit, canModify, onClose, uploadInfo, root}: CardViewProps) => {
  const handleCheckboxClick = canModify
    ? (nextContent: any) => {
        if (!nextContent) return;
        return api.mutate.cards.update({id: card.cardId, content: nextContent});
      }
    : null;

  const [skipClick, setSkipClick] = useState(false);
  const isUnsaved = cardEditPersistStore.useHasStoredValues(card);

  const handleMouseDown = () => {
    if (isSelectionActive()) {
      setSkipClick(true);
    }
  };
  const handleMouseUp = () => {
    setSkipClick(false);
  };
  const clickToEdit = root.loggedInUser?.enableClickToEditCards;
  const handleClick = (e: any) => {
    if (!clickToEdit) return;
    if (canModify && !skipClick) {
      const pos = e?.nativeEvent?.$cdxEvent?.position;
      onEdit(pos);
      e.stopPropagation(); // since there are two listeners: parent/child
    }

    setSkipClick(false);
    e.stopPropagation();
  };

  useGlobalKeyPress({
    key: "Enter",
    disabled: !canModify,
    fn: () => onEdit(),
  });

  const footer = canModify && (
    <DSButton onClick={() => onEdit()} className={css({ml: "auto", pointerEvents: "auto"})}>
      Edit
    </DSButton>
  );

  return (
    <Layout
      onClose={onClose}
      footer={footer}
      closeTooltip="Close"
      onClick={handleClick}
      uploadArea={
        uploadInfo &&
        canModify && (
          <Uploader.DropArea {...uploadInfo.dropAreaProps} label="Attach file to this card" />
        )
      }
    >
      <div
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        className={css(
          {display: "flex", flexDir: "column", flex: "auto", sp: "24px"},
          focusModeStyles.topContent
        )}
      >
        {isUnsaved && (
          <UnsavedChanges
            onOpenEditPanel={onEdit}
            onClearLs={() => cardEditPersistStore.clearStoredValue(card)}
          />
        )}
        <Markdown
          className={css({px: "12px"})}
          projectId={card.deck && card.deck.project.id}
          size="lg"
          parseCheckboxes
          onCheckboxClick={handleCheckboxClick}
          onClick={handleClick}
          autoTitle
          imageViewerKey={`content-${card.cardId}`}
          cardReferenceAddUrlState={{focusMode: "on"}}
        >
          {card.content}
        </Markdown>
        {uploadInfo && <ShowUploads ongoingUploads={uploadInfo?.ongoingUploads} />}
      </div>
    </Layout>
  );
};

type FocusModeProps = Omit<EditorViewProps, "onClose" | "initialEditorState" | "selectContent"> & {
  onClose: (opts?: {text: string}) => void;
  closing: boolean;
  mode: Mode;
  setMode: Dispatch<Mode>;
  canModify: boolean | null;
  uploadInfo?: UploadInfo;
  root: Root;
};

const FocusMode = (props: FocusModeProps) => {
  const {onClose, mode, setMode, closing, canModify, uploadInfo, editOnly, root, ...rest} = props;

  const handleClose = (opts?: {text: string}) => {
    if (mode.type === "edit" && !editOnly) {
      setMode({type: "view"});
    } else {
      onClose(opts);
    }
  };

  useEsc(handleClose, {isDisabled: closing});
  const handleEdit = (pos?: any) => {
    setMode({type: "edit", payload: {selectContent: pos}});
  };

  return mode.type === "edit" ? (
    <EditorView
      {...rest}
      onClose={handleClose}
      initialEditorState={mode.payload?.editorState}
      selectContent={mode.payload?.selectContent}
    />
  ) : (
    <CardView
      card={rest.card}
      canModify={canModify}
      onEdit={handleEdit}
      onClose={handleClose}
      uploadInfo={uploadInfo}
      root={root}
    />
  );
};

const Container = ({revealProps, children}: {revealProps: any; children: ReactNode}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const focusGroupName = "focusMode";
  return (
    <SimplePortal>
      <Col
        position="fixed"
        top="0"
        left="0"
        width="100%"
        height="100%"
        zIndex={2}
        overflow="hidden"
      >
        <FocusLock group={focusGroupName} returnFocus autoFocus={false}>
          <focusLockKeyGroupContext.Provider value={focusGroupName as any}>
            <KeyPressContextProvider>
              <animated.div
                className={css({
                  display: "flex",
                  flexDir: "column",
                  position: "absolute",
                  top: "0",
                  left: "0",
                  width: "100%",
                  height: "100%",
                  bg: "foreground",
                })}
                style={{
                  opacity: revealProps.value,
                  pointerEvents: revealProps.value.to((val: number) =>
                    val > 0.7 ? "initial" : "none"
                  ),
                }}
                ref={nodeRef}
              >
                <Suspense fallback={<DSSpinner size={20} />}>
                  <FullScreenNodeContext.Provider value={nodeRef}>
                    {children}
                  </FullScreenNodeContext.Provider>
                </Suspense>
              </animated.div>
            </KeyPressContextProvider>
          </focusLockKeyGroupContext.Provider>
        </FocusLock>
      </Col>
    </SimplePortal>
  );
};

export type FocusProps = {active: boolean; setActive: (opts?: {editorState?: any}) => void};

type Mode =
  | {type: "view"; payload?: null}
  | {type: "edit"; payload: {editorState?: any; selectContent?: any}};

const useFocusMode = (
  props: Omit<EditorViewProps, "onClose" | "initialEditorState" | "selectContent"> & {
    canModify: boolean | null;
    location: Location<unknown>;
    history: History;
    uploadInfo?: UploadInfo;
    onCloseWithText?: (text: string) => void;
    root: Root;
  }
) => {
  const {location, history, onCloseWithText, root, ...rest} = props;
  const l = location as Location<{focusMode?: "on"}>;
  const isOpen = l.state?.focusMode === "on";
  const handleClose = (opts?: {text: string}) => {
    history.replace({...l, state: {...l.state, focusMode: undefined}});
    if (opts && onCloseWithText) onCloseWithText(opts.text);
  };
  const handleOpen = () => {
    history.push({...l, state: {...l.state, focusMode: "on"}});
  };
  const [mode, setMode] = useState<Mode>(
    props.editOnly ? {type: "edit", payload: {}} : {type: "view"}
  );
  const revealFn = useReveal(isOpen, {config: springConfigs.quickNoBounce} as any);
  const element = revealFn((styles: any) => (
    <Container revealProps={styles}>
      <FocusMode
        onClose={handleClose}
        closing={!isOpen}
        mode={mode}
        setMode={setMode}
        root={root}
        {...rest}
      />
    </Container>
  ));
  const activate = ({editorState}: {editorState?: any} = {}) => {
    setMode(editorState ? {type: "edit", payload: {editorState}} : {type: "view"});
    handleOpen();
  };
  return [element, isOpen, activate] as const;
};
export default useFocusMode;
